import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { environment } from '@environments/environment';
import type {
  EnterAccountSessionParams,
  LoginCredentials,
  LoginResponse,
  LoginSuccessResponse,
  RefreshTokenResponse,
  VerifyMerchantLoginRequest,
  VerifyMerchantLoginResponse,
} from '../models';

/**
 * AuthApi — HTTP calls for authentication.
 *
 * This layer mirrors the published contracts and nothing more. Product constraints (web is
 * password-only, so `gateway` is always `'password'`) belong one layer up in `AuthService`,
 * so that when OTP eventually reaches web there is a single line to change.
 *
 * Security: never stores tokens.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthApi {
  private readonly http = inject(HttpClient);
  private readonly merchantBaseUrl = `${environment.apiUrl}/api_merchant/v1`;

  /**
   * Resolve a username to the account its password should be checked against.
   *
   * The returned `account_id` is always the user's **primary** merchant, even when they
   * belong to several — the merchant they end up working as is decided later.
   */
  verifyMerchantLogin(username: string): Observable<VerifyMerchantLoginResponse> {
    const payload: VerifyMerchantLoginRequest = {
      username,
      type: environment.merchantType,
      device_type: environment.deviceType,
    };

    return this.http
      .post<VerifyMerchantLoginResponse>(
        `${this.merchantBaseUrl}/auth/verify-merchant-login`,
        payload,
      )
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  /**
   * Check the credential.
   *
   * Returns a **union**: a complete session when the user has one merchant, or an account
   * list with no token at all when they have several. Callers must narrow with
   * `isLoginSuccess` before touching `token_data`.
   *
   * A 2xx with `login_successful: false` is a normal outcome here, not an error — do not
   * add an operator that treats it as one.
   */
  login(credentials: LoginCredentials, accountId?: string): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${this.merchantBaseUrl}/auth/login`, {
        password: credentials.password,
        app_version: environment.version,
        web_token: environment.webToken,
        device_type: environment.deviceType,
        ...(accountId && { account_id: accountId }),
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        }),
      );
  }

  /**
   * Phase 2 — open a session against a chosen merchant.
   *
   * Re-sends the credential the user already typed, because the call is stateless: no token
   * was issued by `/auth/login`, so there is nothing else to prove identity with.
   *
   * `gateway` is required by the contract; the endpoint is shared with the mobile OTP flow.
   * `app_version` is deliberately **not** sent — it is not part of this contract even
   * though `/auth/login` accepts it.
   *
   * Resolves with the same payload shape as a successful `/auth/login`, which is why
   * `AuthService` can feed both into one session writer.
   */
  enterAccountSession(params: EnterAccountSessionParams): Observable<LoginSuccessResponse> {
    return this.http
      .post<LoginSuccessResponse>(`${this.merchantBaseUrl}/account/session`, {
        account_id: params.accountId,
        pass: params.credential,
        gateway: params.gateway,
        web_token: environment.webToken,
        device_type: environment.deviceType,
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Account session error:', error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Refresh access token using refresh token from localStorage
   * Returns new access token (refresh token is reused, not rotated)
   */
  refresh(refreshToken: string): Observable<RefreshTokenResponse> {
    return this.http
      .post<RefreshTokenResponse>(`${this.merchantBaseUrl}/auth/refresh-token`, {
        refresh_token: refreshToken,
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Token refresh error:', error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Forgot password
   */
  forgotPassword(email: string): Observable<{ success: boolean; message?: string }> {
    return this.http
      .post<{
        success: boolean;
        message?: string;
      }>(`${this.merchantBaseUrl}/auth/forgot-password`, { email })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Forgot password error:', error);
          return throwError(() => error);
        }),
      );
  }
}
